/**
 * [CCI-17658] Small accessors for the `slices[] + appliedIndex` slice-history
 * model on `AIContentPositionEntry` (see `pm-plugins/ai-session-context/types.ts`).
 *
 * They centralise the `noUncheckedIndexedAccess` fallback so callers always get a
 * concrete `Slice`, and encode the undo/redo availability rules in one place.
 *
 *   - `slices[0]`               = original (pre-AI) content.
 *   - `slices[appliedIndex]`    = the content currently at `[startPos, endPos]`.
 *   - undo available            = `appliedIndex > 0`.
 *   - redo available            = `appliedIndex < slices.length - 1`.
 */
import { slicesEqualIgnoringLocalId } from '@atlaskit/editor-common/collab-agent-review-slice-compare';
import { Slice } from '@atlaskit/editor-prosemirror/model';

import type { AIContentPositionEntry } from '../../../../pm-plugins/ai-session-context/types';

/** The original (pre-AI) slice — `slices[0]`. Falls back to empty if absent. */
export const originalSliceOf = (entry: AIContentPositionEntry): Slice =>
	entry.slices[0] ?? Slice.empty;

/** The currently-applied slice — `slices[appliedIndex]`. Falls back to empty. */
export const appliedSliceOf = (entry: AIContentPositionEntry): Slice =>
	entry.slices[entry.appliedIndex] ?? Slice.empty;

/** Undo is available while the entry is not already at its original slice. */
export const entryCanUndo = (entry: AIContentPositionEntry): boolean => entry.appliedIndex > 0;

/** Redo is available while the entry is not already at its latest slice. */
export const entryCanRedo = (entry: AIContentPositionEntry): boolean =>
	entry.appliedIndex < entry.slices.length - 1;

/**
 * True when the entry carries no reviewable change, i.e. its original slice is
 * identical to its currently-applied slice, ignoring ephemeral `localId`s and
 * layout-only marks such as `breakout` (see `slicesEqualIgnoringLocalId`).
 *
 * The comparison lives in `@atlaskit/editor-common` so this FINE-grained filter
 * and the COARSE collab-edit segment producer (`getAgentEditSegments`) share one
 * definition of "no reviewable change". If they diverged, an edit whose only
 * delta is a `breakout` change could be recorded as a segment (anchoring the
 * modal) yet dropped here (no diff).
 *
 * These entries add nothing to review — they would make the user step through a
 * "change" where nothing changed — so they are filtered out of the working
 * segments before they are stored in plugin state.
 */
export const entryHasNoChange = (entry: AIContentPositionEntry): boolean =>
	slicesEqualIgnoringLocalId(originalSliceOf(entry), appliedSliceOf(entry));
